import { data } from 'react-router';

import { useAppConfig } from '#/config/AppConfigContext';
import { useTrackPageView } from '#/hooks/useTrackPageView';
import { RouteModule } from '#/horizon/components/RouteModule';
import { useI18n } from '#/i18n/hooks/useI18n';
import { apiIsomorphicFetchUserProfile } from '#/modules/userProfile/api/apiFetchUserProfile';
import { viewUserProfileRoute } from '#/modules/userProfile/routing/userProfile.routes';
import type { HorizonAppRouteLoaderArgs } from '#/routing/classes/types/AppRouteLoaderArgs';
import type { HorizonAppRouteComponentProps } from '#/routing/types/HorizonAppRouteComponentProps';
import { imageUrlForOgMeta } from '#/utils/filestack';

import { ViewUserProfilePageLoadable } from './ViewUserProfilePageLoadable';
import { ViewUserProfilePageNotFoundLoadable } from './ViewUserProfilePageNotFoundLoadable';

export async function loader({
  request,
  context,
  params: { username },
}: HorizonAppRouteLoaderArgs<typeof viewUserProfileRoute>) {
  const [fetchedUserProfile] = await Promise.all([
    apiIsomorphicFetchUserProfile({ request, context })(username).catch(
      () => null,
    ),
    ViewUserProfilePageLoadable.load(),
  ]);

  const userProfile =
    fetchedUserProfile?.type === 'USER' ? fetchedUserProfile : null;

  if (!userProfile) await ViewUserProfilePageNotFoundLoadable.load();

  return data({ userProfile }, { status: userProfile ? 200 : 404 });
}

export default function ViewUserProfileHorizonRoute({
  params: { username },
  loaderData: { userProfile },
}: HorizonAppRouteComponentProps<typeof loader>) {
  const { appConfig } = useAppConfig();
  const { i18n } = useI18n();
  useTrackPageView('User Profile', { username });

  if (!userProfile) {
    return (
      <RouteModule title={i18n('Not Found')}>
        <ViewUserProfilePageNotFoundLoadable />
      </RouteModule>
    );
  }

  return (
    <RouteModule
      title={`${userProfile.name} (@${userProfile.username})`}
      ogImage={imageUrlForOgMeta({ data: userProfile.image, appConfig })}
    >
      <ViewUserProfilePageLoadable initialUserProfile={userProfile} />
    </RouteModule>
  );
}
